// packages/editor/src/studio/DiffTab.tsx
//
// Fetches `git diff -- <path>` from the projector and renders the
// unified-diff text in a styled <pre>. Re-fetches when the path or
// the saved-version-counter changes. E2.5+ could swap the <pre> for a
// proper line-by-line diff component.

import { useEffect, useState } from 'react';
import type { SaveAdapter } from '../save-adapters/types';

export interface DiffTabProps {
  adapter: SaveAdapter;
  path: string;
  /** Bumped by the parent each time a save lands so the diff refreshes. */
  saveCounter: number;
}

export function DiffTab({ adapter, path, saveCounter }: DiffTabProps) {
  const [diff, setDiff] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    adapter.diff(path)
      .then((r) => { if (!cancelled) setDiff(r.unifiedDiff); })
      .catch((e: Error) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [adapter, path, saveCounter]);

  if (error) return <p data-testid="diff-error" style={{ color: 'crimson', padding: 16 }}>{error}</p>;
  if (diff === null) return <p style={{ padding: 16, color: '#888' }}>Loading…</p>;
  if (diff === '') return <p data-testid="diff-empty" style={{ padding: 16, color: '#888' }}>No changes since last commit.</p>;

  return (
    <pre
      data-testid="diff-pre"
      style={{
        margin: 0,
        padding: 16,
        fontFamily: 'monospace',
        fontSize: 12,
        lineHeight: 1.5,
        whiteSpace: 'pre-wrap',
        overflow: 'auto',
        height: '100%',
        background: '#fafafa',
      }}
    >
      {diff}
    </pre>
  );
}
